import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext, Store } from '@ngxs/store';
import { catchError, EMPTY, finalize, switchMap, tap, throwError } from 'rxjs';
import { LoadableStatus, SnackbarService, SnackbarType } from '@aiwyn/shared';
import { ResetState } from '../../../shared/state/meta.actions';
import { AppState } from '../../../state/app.state';
import { CRL_STATUS_LABELS, ClientRequestServiceApiBridge } from '../services/client-request-service-api.bridge';
import { CxpCrlCommentsService } from '../services/cxp-crl-comments.service';
import { CxpCrlNoAccessRedirectService } from '../services/cxp-crl-no-access-redirect.service';
import { isCrlNoAccessError } from '../services/utils/crl-no-access-error.utils';
import { CRL_SUBMITTED_ITEM_STATUS, CrlItemFile, CrlItemStatus, CrlRequestDetail } from '../models';
import {
  ClearRequestDetail,
  InviteContactToItems,
  RevokeItemAccess,
  LoadItemActivity,
  LoadItemActivityFailure,
  LoadItemActivitySuccess,
  LoadItemComments,
  LoadItemCommentsFailure,
  LoadItemCommentsSuccess,
  LoadItemDetail,
  LoadItemDetailFailure,
  LoadItemDetailSuccess,
  LoadRequestDetail,
  LoadRequestDetailFailure,
  LoadRequestDetailSuccess,
  MarkItemCommentsViewed,
  MarkItemFinished,
  MarkItemFinishedFailure,
  MarkItemFinishedSuccess,
  MarkItemNotApplicable,
  MarkItemNotApplicableFailure,
  MarkItemNotApplicableSubmitFailure,
  MarkItemNotApplicableSuccess,
  UndoItemNotApplicable,
  UndoItemNotApplicableFailure,
  UndoItemNotApplicableSuccess,
  PostItemComment,
  RemoveFileFromItem,
  RemoveFileFromItemFailure,
  RemoveFileFromItemSuccess,
  SaveItemAnswer,
  SaveItemAnswerFailure,
  SaveItemAnswerSuccess,
  SetSelectedItemId,
  SetSelectedRequestId,
  UndoItemFinished,
  UndoItemFinishedFailure,
  UndoItemFinishedSuccess,
} from './cxp-crl-detail.actions';
import { CrlItemOptimisticPatch, CxpCrlDetailStateModel, DEFAULT_CXP_CRL_DETAIL_STATE } from './cxp-crl-detail.model';
import { SetCxpActiveRouteParams } from './cxp-tasks.actions';

export const CXP_CRL_DETAIL_STATE_NAME = 'cxpCrlDetail';

@State<CxpCrlDetailStateModel>({
  name: CXP_CRL_DETAIL_STATE_NAME,
  defaults: DEFAULT_CXP_CRL_DETAIL_STATE,
})
@Injectable()
export class CxpCrlDetailState {
  private readonly store = inject(Store);
  private readonly bridge = inject(ClientRequestServiceApiBridge);
  private readonly commentsService = inject(CxpCrlCommentsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly noAccessRedirect = inject(CxpCrlNoAccessRedirectService);

  @Action(ResetState)
  resetState({ setState }: StateContext<CxpCrlDetailStateModel>): void {
    setState(DEFAULT_CXP_CRL_DETAIL_STATE);
  }

  /**
   * The CRL detail + item route params flow in via the shared
   * `SetCxpActiveRouteParams` action (dispatched by `CxpRouteParamsService` on
   * every navigation), mirroring how `CxpDeliverableDetailState` learns its
   * `deliverableId`. We delegate to `SetSelectedRequestId` / `SetSelectedItemId`
   * so the null-clears behavior stays in one place.
   */
  @Action(SetCxpActiveRouteParams)
  setActiveRouteParams(ctx: StateContext<CxpCrlDetailStateModel>, { requestId, itemId }: SetCxpActiveRouteParams) {
    ctx.dispatch(new SetSelectedRequestId(requestId));
    ctx.dispatch(new SetSelectedItemId(itemId));
  }

  @Action(SetSelectedRequestId)
  setSelectedRequestId(ctx: StateContext<CxpCrlDetailStateModel>, { id }: SetSelectedRequestId) {
    ctx.patchState({ selectedRequestId: id });

    if (id === null) {
      ctx.dispatch(new ClearRequestDetail());
    }
  }

  @Action(SetSelectedItemId)
  setSelectedItemId(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: SetSelectedItemId) {
    ctx.patchState({ selectedItemId: itemId });
  }

  @Action(LoadRequestDetail)
  loadRequestDetail(ctx: StateContext<CxpCrlDetailStateModel>, { id }: LoadRequestDetail) {
    const state = ctx.getState();

    // Keep the current detail through the loading state when re-loading the
    // same request; clear it only when switching to a different one.
    const detailToKeep = state.detailData?.id === id ? state.detailData : null;

    ctx.patchState({ detailStatus: LoadableStatus.LOADING, detailData: detailToKeep });

    return this.bridge.getRequestDetail(id).pipe(
      tap((detail) => ctx.dispatch(new LoadRequestDetailSuccess(detail))),
      catchError((error) => {
        ctx.dispatch(new LoadRequestDetailFailure(error));
        return EMPTY;
      }),
    );
  }

  @Action(LoadRequestDetailSuccess)
  loadRequestDetailSuccess(ctx: StateContext<CxpCrlDetailStateModel>, { detail }: LoadRequestDetailSuccess) {
    // Drop responses for a request the user has since navigated away from — a
    // slower in-flight load for the PREVIOUS request resolving after a newer
    // selection would otherwise overwrite it with stale data.
    if (detail.id !== ctx.getState().selectedRequestId) return;

    // Fresh server truth supersedes any optimistic item patches. Assignee
    // names/emails arrive resolved on the `/page` read, so there's no separate
    // contact-directory lookup to fire here.
    ctx.patchState({
      detailStatus: LoadableStatus.LOADED,
      detailData: this.withLoadedItemDetail(detail, ctx.getState().detailData),
      optimisticItemPatches: {},
    });

    // The `/page` read carries no files/answers, so the open item's lazy detail is
    // re-hydrated after a reload — unless one is already in flight, which will
    // deliver the same thing. Opening an item starts a load from the overlay, and
    // this would otherwise start a second.
    const { selectedItemId, itemDetailLoadingItemId } = ctx.getState();
    if (selectedItemId !== null && itemDetailLoadingItemId !== selectedItemId) {
      ctx.dispatch(new LoadItemDetail(selectedItemId));
    }
  }

  /**
   * A refused read (403/404/legacy 400 — the caller cannot access this
   * request) redirects to the tasks list instead of stranding the caller on a
   * broken page; any other failure (500, network) keeps the error state.
   */
  @Action(LoadRequestDetailFailure)
  loadRequestDetailFailure(ctx: StateContext<CxpCrlDetailStateModel>, { error }: LoadRequestDetailFailure) {
    ctx.patchState({ detailStatus: LoadableStatus.ERROR });
    if (isCrlNoAccessError(error)) {
      this.noAccessRedirect.redirectToTasks();
    }
  }

  @Action(ClearRequestDetail)
  clearRequestDetail(ctx: StateContext<CxpCrlDetailStateModel>) {
    ctx.patchState({
      detailStatus: LoadableStatus.DEFAULT,
      detailData: null,
      optimisticItemPatches: {},
      pendingUndoItemIds: [],
      itemActivityByItem: {},
      itemActivityLoadingItemId: null,
      itemCommentsByItem: {},
      itemCommentsLoadingItemId: null,
      itemDetailLoadingItemId: null,
      answerSaveFailedItemIds: [],
    });
  }

  /**
   * Loads the selected item's activity feed from the dedicated per-item endpoint
   * (bridge → REST). The item NAME is read from the loaded detail here (state
   * owns that lookup) and passed to the mapper so SENT/APPROVED render their teal
   * linked object. `itemActivityLoadingItemId` drives the feed's loading state.
   */
  @Action(LoadItemActivity)
  loadItemActivity(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: LoadItemActivity) {
    ctx.patchState({ itemActivityLoadingItemId: itemId });

    return this.bridge.getItemActivity(itemId, this.currentItemName(ctx, itemId)).pipe(
      tap((entries) => ctx.dispatch(new LoadItemActivitySuccess(itemId, entries))),
      catchError((error) => {
        ctx.dispatch(new LoadItemActivityFailure(itemId, error));
        return EMPTY;
      }),
    );
  }

  @Action(LoadItemActivitySuccess)
  loadItemActivitySuccess(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, entries }: LoadItemActivitySuccess) {
    // Cache the feed under its item id regardless of the current selection — a
    // load that resolves after the user moved on still populates that item's
    // cache for when they return. Only clear the loading flag if THIS load owns it.
    const state = ctx.getState();
    ctx.patchState({
      itemActivityByItem: { ...state.itemActivityByItem, [itemId]: entries },
      itemActivityLoadingItemId: state.itemActivityLoadingItemId === itemId ? null : state.itemActivityLoadingItemId,
    });
  }

  @Action(LoadItemActivityFailure)
  loadItemActivityFailure(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: LoadItemActivityFailure) {
    // Only clear the loading flag if THIS load still owns it (a newer load may
    // have superseded it). Any previously-cached feed for the item is preserved.
    if (itemId === ctx.getState().itemActivityLoadingItemId) {
      ctx.patchState({ itemActivityLoadingItemId: null });
    }
    console.error(`[CxpCrlDetail] item activity load failed for item ${itemId}`, error);
  }

  /**
   * Loads (or refreshes) the selected item's comments thread via the comments
   * facade, caching the result under the item id. Sets `itemCommentsLoadingItemId`
   * up front; the already-cached thread stays visible until this load lands
   * (stale-while-revalidate), mirroring the activity feed.
   */
  @Action(LoadItemComments)
  loadItemComments(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: LoadItemComments) {
    ctx.patchState({ itemCommentsLoadingItemId: itemId });

    return this.commentsService.loadComments(itemId).pipe(
      tap((comments) => ctx.dispatch(new LoadItemCommentsSuccess(itemId, comments))),
      catchError((error) => {
        ctx.dispatch(new LoadItemCommentsFailure(itemId, error));
        return EMPTY;
      }),
    );
  }

  @Action(LoadItemCommentsSuccess)
  loadItemCommentsSuccess(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, comments }: LoadItemCommentsSuccess) {
    const state = ctx.getState();
    ctx.patchState({
      itemCommentsByItem: { ...state.itemCommentsByItem, [itemId]: comments },
      itemCommentsLoadingItemId: state.itemCommentsLoadingItemId === itemId ? null : state.itemCommentsLoadingItemId,
    });
  }

  @Action(LoadItemCommentsFailure)
  loadItemCommentsFailure(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: LoadItemCommentsFailure) {
    if (itemId === ctx.getState().itemCommentsLoadingItemId) {
      ctx.patchState({ itemCommentsLoadingItemId: null });
    }
    console.error(`[CxpCrlDetail] item comments load failed for item ${itemId}`, error);
  }

  /**
   * Posts a comment/reply then refreshes the item's cached thread. The refresh
   * reuses the same success path so ordering + reply nesting stay authoritative.
   * The failure is surfaced (the composer keeps the typed text) but the cached
   * thread is left intact.
   */
  @Action(PostItemComment)
  postItemComment(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, text, parentId }: PostItemComment) {
    return this.commentsService.postComment(itemId, text, parentId).pipe(
      switchMap(() => this.commentsService.loadComments(itemId)),
      tap((comments) => ctx.dispatch(new LoadItemCommentsSuccess(itemId, comments))),
      catchError((error) => {
        console.error(`[CxpCrlDetail] item comment post failed for item ${itemId}`, error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * Records the caller as having viewed comments of an item, in one batched write
   * covering every contact id linked to them. `AppState.getUserContactIds` is the
   * email-resolved SET — a user can hold several contact records, the read filters
   * run over all of them, and writing only one leaves the row's dot and the
   * thread's own dot disagreeing. Nothing is patched locally: the comments live
   * query re-emits and clears the row's dot on its own.
   */
  @Action(MarkItemCommentsViewed)
  markItemCommentsViewed(_: StateContext<CxpCrlDetailStateModel>, { itemId, commentIds }: MarkItemCommentsViewed) {
    const contactIds = this.store.selectSnapshot(AppState.getUserContactIds) ?? [];
    if (commentIds.length === 0 || contactIds.length === 0) return EMPTY;

    return this.commentsService.markCommentsViewed(commentIds, contactIds).pipe(
      catchError((error) => {
        console.error(`[CxpCrlDetail] marking comments viewed failed for item ${itemId}`, error);
        return EMPTY;
      }),
    );
  }

  /**
   * Lazily loads the selected item's files + answer (bridge → `/item/detail`) and
   * patches them onto that one item inside `detailData`. `itemDetailLoadingItemId`
   * drives the per-item loading state; a slower load for a since-abandoned item is
   * dropped at success time.
   */
  @Action(LoadItemDetail)
  loadItemDetail(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: LoadItemDetail) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;

    ctx.patchState({ itemDetailLoadingItemId: itemId });

    return this.bridge.getItemDetail(requestId, itemId).pipe(
      tap(({ files, answer, isAnswerMasked }) => ctx.dispatch(new LoadItemDetailSuccess(itemId, files, answer, isAnswerMasked))),
      catchError((error) => {
        ctx.dispatch(new LoadItemDetailFailure(itemId, error));
        return EMPTY;
      }),
    );
  }

  @Action(LoadItemDetailSuccess)
  loadItemDetailSuccess(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, files, answer, isAnswerMasked }: LoadItemDetailSuccess) {
    // Drop a slower in-flight load for an item the user has since navigated away
    // from — a newer selection's load owns the slice + loading flag.
    if (itemId !== ctx.getState().selectedItemId) return;
    this.setItemDetail(ctx, itemId, files, answer, isAnswerMasked);
    ctx.patchState({ itemDetailLoadingItemId: null });
  }

  /**
   * A refused item read redirects ONLY for a deep link: an item absent from the
   * loaded page is one the caller reached by URL, not by clicking a rendered
   * row (the page returns exactly the items the caller may see). An in-page
   * selection that fails — whatever the status — keeps the existing behavior.
   */
  @Action(LoadItemDetailFailure)
  loadItemDetailFailure(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: LoadItemDetailFailure) {
    if (itemId === ctx.getState().itemDetailLoadingItemId) {
      ctx.patchState({ itemDetailLoadingItemId: null });
    }
    console.error(`[CxpCrlDetail] item detail load failed for item ${itemId}`, error);
    if (isCrlNoAccessError(error) && !this.isItemInLoadedDetail(ctx, itemId)) {
      this.noAccessRedirect.redirectToTasks();
    }
  }

  /**
   * Save a questionnaire answer (panel "save on blur"). A SILENT draft persist:
   * the backend's `ClientRequestSubmitAnswers` stores the answer without
   * changing the item status (transitions happen only via mark-as-finished /
   * undo), so no status patch is applied here. Success writes the stored answer
   * onto the item itself, so there is no reload either.
   */
  @Action(SaveItemAnswer)
  saveItemAnswer(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, answer }: SaveItemAnswer) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;

    // This attempt supersedes the last one's outcome.
    this.setAnswerSaveFailed(ctx, itemId, false);

    return this.bridge.saveItemAnswer(requestId, itemId, answer).pipe(
      tap(() => ctx.dispatch(new SaveItemAnswerSuccess(itemId, answer))),
      catchError((error) => {
        ctx.dispatch(new SaveItemAnswerFailure(itemId, error));
        return EMPTY;
      }),
    );
  }

  /**
   * Writes the stored text onto the item so every surface reads back what was
   * sent. It goes into the loaded detail rather than into a patch, because a
   * reload clears patches while carrying no answers of its own to replace them
   * with — the card would swap to its read-only form showing an empty answer
   * until `/item/detail` caught up.
   *
   * No reload follows. A blur-triggered draft persist changes nothing a reload
   * would report: the endpoint leaves the status alone, cannot touch files, and
   * the answer is written above. Reloading here would also race the finish that
   * can follow a blur — `/page` clears the patch mark-as-finished had just
   * applied and returns an item read before it was finished, putting the card
   * back to editable until the finish's own reload arrived.
   */
  @Action(SaveItemAnswerSuccess)
  saveItemAnswerSuccess(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, answer }: SaveItemAnswerSuccess) {
    this.setItemAnswer(ctx, itemId, answer);
    this.setAnswerSaveFailed(ctx, itemId, false);
  }

  /**
   * A failed save leaves the client's text in the (dirty) input, so there is
   * nothing to roll back and the next blur retries. It cannot stay silent,
   * though: the answer they can still see is not the answer the firm has, so the
   * item is held back from review until a save gets through.
   */
  @Action(SaveItemAnswerFailure)
  saveItemAnswerFailure(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: SaveItemAnswerFailure) {
    console.error(`[CxpCrlDetail] answer save failed for item ${itemId}`, error);
    this.setAnswerSaveFailed(ctx, itemId, true);
    this.snackbarService.open('Your answer could not be saved. Please try again.', SnackbarType.ERROR);
  }

  /**
   * Removes an uploaded client file from an item (the row "X"). The remove
   * endpoint returns no reloaded detail, so we optimistically drop the file
   * from `detailData` and roll the whole file list back on failure. Direct
   * `detailData` edit (not an `optimisticItemPatches` entry) since there is no
   * reload to reconcile against.
   */
  @Action(RemoveFileFromItem)
  removeFileFromItem(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, fileId }: RemoveFileFromItem) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;

    const previousFiles = this.currentItemFiles(ctx, itemId);
    this.setItemFiles(ctx, itemId, previousFiles.filter((file) => file.fileId !== fileId));

    return this.bridge.removeFileFromItem(requestId, itemId, fileId).pipe(
      tap(() => ctx.dispatch(new RemoveFileFromItemSuccess(itemId, fileId))),
      catchError((error) => {
        this.setItemFiles(ctx, itemId, previousFiles);
        ctx.dispatch(new RemoveFileFromItemFailure(itemId, fileId, error));
        return EMPTY;
      }),
    );
  }

  @Action(RemoveFileFromItemSuccess)
  removeFileFromItemSuccess() {
    this.snackbarService.open('File removed.', SnackbarType.SUCCESS);
  }

  @Action(RemoveFileFromItemFailure)
  removeFileFromItemFailure(_ctx: StateContext<CxpCrlDetailStateModel>, { itemId, fileId, error }: RemoveFileFromItemFailure) {
    console.error(`[CxpCrlDetail] file remove failed for item ${itemId}, file ${fileId}`, error);
    this.snackbarService.open('That file could not be removed. Please try again.', SnackbarType.ERROR);
  }

  /**
   * "I'm Finished" → In Review, via the bridge, then a detail reload to reconcile.
   * The card is left exactly as it is until the call returns: the client waits
   * rather than being shown a submission the server has not accepted, so a
   * failure has nothing to take back.
   *
   * The endpoint writes the raw `IN_REVIEW` status; what the client sees is
   * `CRL_SUBMITTED_ITEM_STATUS`, shared with the bridge's read mapping so the
   * patched view and the reloaded one cannot disagree. `isAwaitingReview` is
   * patched alongside it, which is what offers Undo before the reload lands.
   */
  @Action(MarkItemFinished)
  markItemFinished(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: MarkItemFinished) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;

    return this.bridge.markItemFinished(requestId, itemId).pipe(
      tap(() => {
        // Patched inside the response rather than in the Success handler, so it
        // is in place before this call reports back. The caller holding the card
        // inert lets go the moment it does, and would otherwise let go onto the
        // old state for as long as the dispatch took to be handled.
        this.applyItemPatch(ctx, itemId, {
          status: CRL_SUBMITTED_ITEM_STATUS,
          statusLabel: CRL_STATUS_LABELS[CRL_SUBMITTED_ITEM_STATUS],
          isAwaitingReview: true,
        });
        ctx.dispatch(new MarkItemFinishedSuccess(itemId));
      }),
      catchError((error) => {
        ctx.dispatch(new MarkItemFinishedFailure(itemId, error));
        return EMPTY;
      }),
    );
  }

  /** The patch is already in place; this reconciles it against server truth. */
  @Action(MarkItemFinishedSuccess)
  markItemFinishedSuccess(ctx: StateContext<CxpCrlDetailStateModel>) {
    return this.reloadCurrentDetail(ctx);
  }

  /** Nothing moved, so there is nothing to roll back — but silence would read as success. */
  @Action(MarkItemFinishedFailure)
  markItemFinishedFailure(_ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: MarkItemFinishedFailure) {
    console.error(`[CxpCrlDetail] mark-finished failed for item ${itemId}`, error);
    this.snackbarService.open('This item could not be sent for review. Please try again.', SnackbarType.ERROR);
  }

  /**
   * Undo "I'm Finished" → back to open/waiting. Deliberately NOT optimistic
   * (product flow: the card keeps its answered state, with the Undo link
   * disabled via `pendingUndoItemIds`, until the BE confirms). Success ALWAYS
   * reloads the detail for server truth — the endpoint silently skips items the
   * caller isn't assigned to while still reporting success — and only then is
   * the pending flag cleared, so the card flips straight to the reloaded state.
   */
  @Action(UndoItemFinished)
  undoItemFinished(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: UndoItemFinished) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;

    this.setUndoPending(ctx, itemId, true);

    return this.bridge.undoItemFinished(requestId, itemId).pipe(
      tap(() => ctx.dispatch(new UndoItemFinishedSuccess(itemId))),
      catchError((error) => {
        ctx.dispatch(new UndoItemFinishedFailure(itemId, error));
        return EMPTY;
      }),
    );
  }

  @Action(UndoItemFinishedSuccess)
  undoItemFinishedSuccess(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: UndoItemFinishedSuccess) {
    return this.reloadCurrentDetail(ctx).pipe(finalize(() => this.setUndoPending(ctx, itemId, false)));
  }

  // Not optimistic, so nothing in the view changes on failure: the message is
  // the only thing separating a failed undo from one that never fired.
  @Action(UndoItemFinishedFailure)
  undoItemFinishedFailure(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: UndoItemFinishedFailure) {
    this.setUndoPending(ctx, itemId, false);
    console.error(`[CxpCrlDetail] undo-finished failed for item ${itemId}`, error);
    this.snackbarService.open('This item could not be reopened. Please try again.', SnackbarType.ERROR);
  }

  /**
   * "Mark as N/A" with optional note — optimistic, then two calls, then reload.
   *
   * Marking an item not-applicable sends it to the firm for review, the same
   * destination as "I'm Finished". It takes two calls because the N/A mutation
   * only annotates the item: it sets `isNotApplicable` and marks it ready to
   * submit, leaving the status alone. `markItemFinished` performs the transition
   * to In Review.
   *
   * The two are not atomic, and the failure modes are deliberately different. If
   * the annotation fails, nothing happened and the optimistic patch rolls back.
   * If the annotation lands but the transition does not, the item **is**
   * not-applicable on the server — rolling back would show the user a clean item
   * that turns N/A again on the next load — so that case keeps the change,
   * reconciles with the server and reports the failure.
   */
  @Action(MarkItemNotApplicable)
  markItemNotApplicable(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, note }: MarkItemNotApplicable) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;

    return this.bridge.markItemNotApplicable(itemId, true, note).pipe(
      switchMap(() =>
        this.bridge.markItemFinished(requestId, itemId).pipe(
          catchError((error) => {
            ctx.dispatch(new MarkItemNotApplicableSubmitFailure(itemId, error));
            return EMPTY;
          }),
        ),
      ),
      // Patched before this call reports back, as in `markItemFinished`.
      tap(() => {
        this.applyItemPatch(ctx, itemId, {
          status: CrlItemStatus.NOT_APPLICABLE,
          statusLabel: CRL_STATUS_LABELS[CrlItemStatus.NOT_APPLICABLE],
          isNotApplicable: true,
          notApplicableReason: note,
        });
        ctx.dispatch(new MarkItemNotApplicableSuccess(itemId));
      }),
      catchError((error) => {
        ctx.dispatch(new MarkItemNotApplicableFailure(itemId, error));
        return EMPTY;
      }),
    );
  }

  /** Both calls landed and the patch is in place; this reconciles it against server truth. */
  @Action(MarkItemNotApplicableSuccess)
  markItemNotApplicableSuccess(ctx: StateContext<CxpCrlDetailStateModel>) {
    return this.reloadCurrentDetail(ctx);
  }

  /** The annotation was refused, so the item never changed. */
  @Action(MarkItemNotApplicableFailure)
  markItemNotApplicableFailure(_ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: MarkItemNotApplicableFailure) {
    console.error(`[CxpCrlDetail] mark-N/A failed for item ${itemId}`, error);
    this.snackbarService.open('This item could not be marked N/A. Please try again.', SnackbarType.ERROR);
  }

  /**
   * The annotation landed but the submission did not: the item IS not-applicable
   * and never left the client's court. Reload for the server's own account of it,
   * and say so — silence would leave the client believing the firm has it.
   */
  @Action(MarkItemNotApplicableSubmitFailure)
  markItemNotApplicableSubmitFailure(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: MarkItemNotApplicableSubmitFailure) {
    console.error(`[CxpCrlDetail] mark-N/A submit failed for item ${itemId}`, error);
    this.snackbarService.open('This item was marked N/A but could not be sent for review. Please try again.', SnackbarType.ERROR);
    return this.reloadCurrentDetail(ctx);
  }

  /**
   * Undo "Mark as N/A" → the item returns to its open state.
   *
   * Marking N/A makes two calls — annotate, then send for review — so undoing it
   * reverses both, in the opposite order: pull the item back out of In Review,
   * then clear the flag. Clearing the flag alone would leave the item sitting
   * with the firm as an ordinary submitted item, which is not a state the user
   * asked for.
   *
   * The order matters for the half-failure. Un-submitting first means a failure
   * on the second call leaves the item out of review but still flagged N/A — the
   * row still reads N/A, which is still true, and the same Undo link retries the
   * whole thing. The other order would strand the item as submitted-but-not-N/A,
   * a state the user never asked for.
   *
   * Deliberately NOT optimistic: the restored status is the server's to decide,
   * so the row keeps its N/A state with the Undo link disabled
   * (`pendingUndoItemIds`) until the reload lands, mirroring undo-finished.
   */
  @Action(UndoItemNotApplicable)
  undoItemNotApplicable(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: UndoItemNotApplicable) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;

    this.setUndoPending(ctx, itemId, true);

    return this.bridge.undoItemFinished(requestId, itemId).pipe(
      switchMap(() => this.bridge.markItemNotApplicable(itemId, false, null)),
      tap(() => ctx.dispatch(new UndoItemNotApplicableSuccess(itemId))),
      catchError((error) => {
        ctx.dispatch(new UndoItemNotApplicableFailure(itemId, error));
        return EMPTY;
      }),
    );
  }

  @Action(UndoItemNotApplicableSuccess)
  undoItemNotApplicableSuccess(ctx: StateContext<CxpCrlDetailStateModel>, { itemId }: UndoItemNotApplicableSuccess) {
    return this.reloadCurrentDetail(ctx).pipe(finalize(() => this.setUndoPending(ctx, itemId, false)));
  }

  // As with undo-finished: the row is left exactly as it was clicked, so only
  // the message tells the user the undo failed.
  @Action(UndoItemNotApplicableFailure)
  undoItemNotApplicableFailure(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, error }: UndoItemNotApplicableFailure) {
    this.setUndoPending(ctx, itemId, false);
    console.error(`[CxpCrlDetail] undo-not-applicable failed for item ${itemId}`, error);
    this.snackbarService.open('This item is still marked N/A. Please try again.', SnackbarType.ERROR);
  }

  /**
   * Invites a firm contact (by email) to every item the dialog selected. NOT
   * optimistic — the resolved contact is unknown until the detail reloads — so on
   * success we reload the request detail so the newly-added contact appears in
   * the rail's Client access. Errors are re-thrown so the dialog (which
   * subscribes to the dispatch) surfaces them and stays open.
   */
  @Action(InviteContactToItems)
  inviteContactToItems(ctx: StateContext<CxpCrlDetailStateModel>, { itemIds, email, displayName, message }: InviteContactToItems) {
    const requestId = ctx.getState().selectedRequestId;
    // An empty array is not a safe no-op downstream — the sibling revoke
    // endpoint reads one as "the whole request" — so it never reaches the bridge.
    if (requestId === null || itemIds.length === 0) return EMPTY;

    // Single assignee per invite; the payload/endpoint accept an array of them.
    return this.bridge.inviteToItems(requestId, itemIds, [{ email, displayName }], message).pipe(
      tap(() => ctx.dispatch(new LoadRequestDetail(requestId))),
    );
  }

  /**
   * Removes one person from one item. The reload confirms it, not the response —
   * the endpoint answers `true` even when nothing matched. A failure reloads too,
   * so a row never disappears on a revoke that did not happen.
   */
  @Action(RevokeItemAccess)
  revokeItemAccess(ctx: StateContext<CxpCrlDetailStateModel>, { itemId, target }: RevokeItemAccess) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;

    return this.bridge.revokeFromItem(requestId, itemId, target).pipe(
      tap(() => ctx.dispatch(new LoadRequestDetail(requestId))),
      catchError(() => {
        this.snackbarService.open('Could not remove this person. Please try again.', SnackbarType.ERROR);
        ctx.dispatch(new LoadRequestDetail(requestId));
        return EMPTY;
      }),
    );
  }

  /** Reloads the currently selected request's detail (post-mutation reconcile). */
  private reloadCurrentDetail(ctx: StateContext<CxpCrlDetailStateModel>) {
    const requestId = ctx.getState().selectedRequestId;
    if (requestId === null) return EMPTY;
    return ctx.dispatch(new LoadRequestDetail(requestId));
  }

  /** Whether the loaded detail contains this item — i.e. the caller could see its row. */
  private isItemInLoadedDetail(ctx: StateContext<CxpCrlDetailStateModel>, itemId: number): boolean {
    return !!ctx.getState().detailData?.sections.some((section) => section.items.some((item) => item.id === itemId));
  }

  /** Name of an item in the loaded detail (drives the activity feed's linked object). */
  private currentItemName(ctx: StateContext<CxpCrlDetailStateModel>, itemId: number): string | null {
    const item = ctx
      .getState()
      .detailData?.sections.flatMap((section) => section.items)
      .find((entry) => entry.id === itemId);
    return item?.name ?? null;
  }

  /** Current files nested under an item in the loaded detail (empty when absent). */
  private currentItemFiles(ctx: StateContext<CxpCrlDetailStateModel>, itemId: number): CrlItemFile[] {
    const item = ctx
      .getState()
      .detailData?.sections.flatMap((section) => section.items)
      .find((entry) => entry.id === itemId);
    return item?.files ?? [];
  }

  /** Replaces the files array of one item in `detailData` (immutable rebuild). */
  private setItemFiles(ctx: StateContext<CxpCrlDetailStateModel>, itemId: number, files: CrlItemFile[]) {
    const detailData = ctx.getState().detailData;
    if (!detailData) return;
    ctx.patchState({
      detailData: {
        ...detailData,
        sections: detailData.sections.map((section) => ({
          ...section,
          items: section.items.map((item) => (item.id === itemId ? { ...item, files } : item)),
        })),
      },
    });
  }

  /**
   * Re-attaches the lazily-loaded per-item fields (files + answer) to a freshly
   * read `/page` detail, which does not carry them (immutable rebuild).
   *
   * Without this, every reload empties the open item's Documents tab and answer
   * for the round trip `/item/detail` takes to follow — most visible right after
   * an upload, where the file just added and every file already listed vanish
   * together. Carrying the last known values forward makes the reload
   * stale-while-revalidate, as the activity and comment caches already are.
   *
   * Only values that exist are carried, so an item whose detail was never fetched
   * is left exactly as the server sent it.
   */
  private withLoadedItemDetail(detail: CrlRequestDetail, previous: CrlRequestDetail | null): CrlRequestDetail {
    if (!previous) return detail;

    const loaded = new Map(
      previous.sections
        .flatMap((section) => section.items)
        .map((item) => [item.id, item] as const),
    );

    return {
      ...detail,
      sections: detail.sections.map((section) => ({
        ...section,
        items: section.items.map((item) => {
          const before = loaded.get(item.id);
          if (!before) return item;
          return {
            ...item,
            ...(before.files?.length ? { files: before.files } : {}),
            ...(before.answer !== undefined ? { answer: before.answer, isAnswerMasked: before.isAnswerMasked } : {}),
          };
        }),
      })),
    };
  }

  /** Records (or lifts) an item's unsaved-answer flag. */
  private setAnswerSaveFailed(ctx: StateContext<CxpCrlDetailStateModel>, itemId: number, failed: boolean) {
    const current = ctx.getState().answerSaveFailedItemIds ?? [];
    if (failed === current.includes(itemId)) return;
    ctx.patchState({
      answerSaveFailedItemIds: failed ? [...current, itemId] : current.filter((id) => id !== itemId),
    });
  }

  /** Replaces one item's answer in the loaded detail (immutable rebuild). */
  private setItemAnswer(ctx: StateContext<CxpCrlDetailStateModel>, itemId: number, answer: string) {
    const detailData = ctx.getState().detailData;
    if (!detailData) return;
    ctx.patchState({
      detailData: {
        ...detailData,
        sections: detailData.sections.map((section) => ({
          ...section,
          items: section.items.map((item) => (item.id === itemId ? { ...item, answer } : item)),
        })),
      },
    });
  }

  private setItemDetail(
    ctx: StateContext<CxpCrlDetailStateModel>,
    itemId: number,
    files: CrlItemFile[],
    answer: string,
    isAnswerMasked: boolean,
  ) {
    const detailData = ctx.getState().detailData;
    if (!detailData) return;
    ctx.patchState({
      detailData: {
        ...detailData,
        sections: detailData.sections.map((section) => ({
          ...section,
          items: section.items.map((item) => (item.id === itemId ? { ...item, files, answer, isAnswerMasked } : item)),
        })),
      },
    });
  }

  /** Merges an optimistic patch for one item (cxp-state: merge, never overwrite). */
  private applyItemPatch(ctx: StateContext<CxpCrlDetailStateModel>, itemId: number, patch: CrlItemOptimisticPatch) {
    const existing = ctx.getState().optimisticItemPatches;
    ctx.patchState({
      optimisticItemPatches: { ...existing, [itemId]: { ...existing[itemId], ...patch } },
    });
  }

  /** Adds/removes an item id on the in-flight undo list (dedup-safe). */
  private setUndoPending(ctx: StateContext<CxpCrlDetailStateModel>, itemId: number, pending: boolean) {
    const current = ctx.getState().pendingUndoItemIds;
    ctx.patchState({
      pendingUndoItemIds: pending ? [...new Set([...current, itemId])] : current.filter((id) => id !== itemId),
    });
  }
}
